<script setup>
import TextField from '@/components/Form/TextField.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import SportBettingFinance from './Partials/SportBettingFinance.vue';
import CasinoFinance from './Partials/CasinoFinance.vue';
import LiveCasinoFinance from './Partials/LiveCasinoFinance.vue';
import NewCasinoFinance from './Partials/NewCasinoFinance.vue';
import { useI18n } from 'vue-i18n';
import { modelDateValue } from '@/helpers';
import { onMounted, ref } from 'vue';

const { t } = useI18n();

// months from i18n
const months = Array.from({ length: 12 }, (_, i) => t(`months.${i}`));

// tabs from i18n
const tabs = ref([
  { 0: t('tabs.0') }, // Today
  { 1: t('tabs.1') }  // Yesterday
]);

const activeTab = ref(0);

const tempFilters = ref({
  sd: modelDateValue(),
  ed: modelDateValue()
});

const filters = ref({
  sd: modelDateValue(),
  ed: modelDateValue()
});

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  return `${year}-${month}-${day}`;
};

const setTab = async (index) => {
  activeTab.value = index;
  const date = new Date();

  switch (index) {
    case 0: // Today
      tempFilters.value.sd = modelDateValue();
      tempFilters.value.ed = modelDateValue();
      break;
    case 1: // Yesterday
      date.setDate(date.getDate() - 1);
      tempFilters.value.sd = date.toISOString().split('T')[0];
      tempFilters.value.ed = date.toISOString().split('T')[0];
      break;
    case 2: // Current month
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      tempFilters.value.sd = formatDate(firstDay);
      tempFilters.value.ed = formatDate(lastDay);
      break;
    case 3: // Previous month
      const fd = new Date(date.getFullYear(), date.getMonth() - 1, 1);
      const ld = new Date(date.getFullYear(), date.getMonth(), 0);
      tempFilters.value.sd = formatDate(fd);
      tempFilters.value.ed = formatDate(ld);
      break;
  }

  applyFilters();
};

const applyFilters = () => {
  filters.value = { ...tempFilters.value };
};

const setCurrentMonth = () => {
  const d = new Date();
  tabs.value.push({ 2: t(`months.${d.getMonth()}`) });
};

const setPreviousMonth = () => {
  const d = new Date();
  let prev = d.getMonth() - 1;
  if (prev < 0) prev = 11;
  tabs.value.push({ 3: t(`months.${prev}`) });
};

onMounted(() => {
  setCurrentMonth();
  setPreviousMonth();
});
</script>

<template>
  <div>
    <form @submit.prevent>
      <div class="px-4 py-2">
        <TextField
          type="date"
          v-model="tempFilters.sd"
          placeholder="Start Date"
          class="w-full rounded-b-none"
        />
        <TextField
          type="date"
          v-model="tempFilters.ed"
          placeholder="End Date"
          class="w-full border-t border-neutral-200 rounded-t-none"
        />
        <DefaultButton
          @click.prevent="applyFilters"
          class="w-full"
        >
          {{ $t('general.26') }}
        </DefaultButton>
      </div>
    </form>

    <div class="h-12 px-4 flex">
      <ul class="list-none flex items-center space-x-2">
        <li
          v-for="tab, i in tabs"
          :key="i"
          :class="['tab-item', { 'active': activeTab == i }]"
          @click="setTab(i)"
        >
          <span>{{ tab[i] }}</span>
        </li>
      </ul>
    </div>

    <div class="mt-4 px-4 space-y-4">
      <div>
        <h3 class="text-sm font-bold text-white/60 mb-2">{{ $t('app.5') }}</h3>
        <div class="fin-Container">
          <table class="fin-Table">
            <SportBettingFinance
              :start-date="filters.sd"
              :end-date="filters.ed"
            />
          </table>
        </div>
      </div>

      <div>
        <h3 class="text-sm font-bold text-white/60 mb-2">{{ $t('app.34') }}</h3>
        <div class="fin-Container">
          <table class="fin-Table">
            <NewCasinoFinance
              :start-date="filters.sd"
              :end-date="filters.ed"
            />
          </table>
        </div>
      </div>

      <div>
        <h3 class="text-sm font-bold text-white/60 mb-2">{{ $t('app.7') }}</h3>
        <div class="fin-Container">
          <table class="fin-Table">
            <LiveCasinoFinance
              :start-date="filters.sd"
              :end-date="filters.ed"
            />
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.fin-Container {
  background-image: var(--mgc-bg);
  @apply rounded-md overflow-hidden;
}

.fin-Table {
  @apply w-full text-neutral-200 divide-y divide-neutral-500/30;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
}

@media (max-width: 640px) {
  .fin-Table {
    font-size: 2.5vw;
    line-height: 7vw;
  }
}

.fin-Table th,
.fin-Table td {
  @apply px-2;
}

.fin-Table th {
  @apply font-normal text-neutral-400;
}

.fin-Table .left {
  @apply text-left;
}
</style>
